/**
 * TaskGroupFormModal — add / edit one task group (Admin > Task Groups), in the
 * corporate dashboard's Task Group modal format:
 *   - Group Name * and Complete Before Due (Allowed / Not Allowed);
 *   - the trigger bar, in the trigger's colour, summarising when tasks are
 *     added; open it for Trigger Type, the Add At list (plan stages, plan
 *     statuses or appointment stages), for an appointment trigger "Any" or
 *     "First Time" appointment, and for appointment / lab / payment triggers
 *     which plan the tasks go on (Apply To, with the plan types);
 *   - Remove At: the plan stages, statuses and appointment stages that take
 *     the tasks off again;
 *   - Filters: who the group applies to (plan type, care team, facility,
 *     procedure, product, membership, package, lead source, language,
 *     appointment type / group / status / facility, tags, other);
 *   - Task Items (TaskItemCard): what is added, when, how often, by whom.
 * Corp's checks block saving (name, the trigger's Add At list, plan types when
 * Apply To needs them, every item's task / user / group); a group without
 * items, or an automated task not assigned to Automatic, is only warned
 * about. Unlike corp, items are added and removed with the rest at Save, and
 * can be added while creating the group. Widths are corp's: plain pickers fit
 * their content, searchable ones fill their column.
 * On a Locked page AdminFormModal makes it a viewer (fields read-only, Close
 * only, no errors or suggestions); this form also drops Add Item, the item
 * remove buttons and its own error and warning wording and colours, silently.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  APPLY_TO_TRIGGERS,
  APPT_STATUS_LABELS,
  ASSIGNED_TO_OPTIONS,
  EVENT_TRIGGERS,
  EXTRA_FILTER_OPTIONS,
  FILTER_KEYS,
  FILTER_OPTION_KEY,
  TRIGGER_TYPE_LABELS,
  TRIGGER_TYPE_OPTIONS,
  csvIds,
  getAdminTaskGroup,
  getTaskGroupFilterOptions,
  listAccountUsers,
  saveTaskGroup,
  type AccountUser,
  type AdminTaskGroup,
  type FilterKey,
  type FilterOption,
  type TaskGroupDraft,
  type TaskGroupFilterOptions,
} from '@/services/taskGroupsAdmin.service';
import { listAdminTasks, type AdminTask } from '@/services/tasksAdmin.service';
import { toast } from '@/store/toastStore';
import { AdminFormModal } from './AdminFormModal';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { DialogField, LABEL_CLASS, ModifiedPill, SelectedRows, fieldClasses } from './DialogChrome';
import { TaskItemCard, itemProblems, wontSend, type FormItem } from './TaskItemCard';
import { useTrackedForm } from './useTrackedForm';

interface GroupForm extends Omit<TaskGroupDraft, 'items'> {
  items: FormItem[];
}

const NO_FILTERS = Object.fromEntries(FILTER_KEYS.map((k) => [k, ''])) as Record<FilterKey, string>;

function emptyForm(): GroupForm {
  return {
    name: '',
    required: false,
    triggerType: 0,
    applyToPlan: false,
    firstAppointmentOnly: false,
    addAtStages: [],
    addAtStatuses: [],
    addAtApptStages: [],
    removeAtStages: [],
    removeAtStatuses: [],
    removeAtApptStages: [],
    filters: { ...NO_FILTERS },
    items: [],
  };
}

/**
 * The group as a form. A stored filter id that its list no longer offers (a
 * retired plan type, a deleted tag) is dropped, as corp does — unless the
 * list came back empty, which may only mean it failed to load.
 */
function formFrom(g: AdminTaskGroup, opts: TaskGroupFilterOptions): GroupForm {
  const ids = (list: { value: number; remove: boolean }[], remove: boolean) => list.filter((x) => x.remove === remove).map((x) => String(x.value));
  const filters = { ...NO_FILTERS };
  for (const key of FILTER_KEYS) {
    const optionKey = FILTER_OPTION_KEY[key];
    const list = optionKey ? opts[optionKey] : [];
    const stored = csvIds(g.filters[key]);
    filters[key] = (optionKey && list.length > 0 ? stored.filter((id) => list.some((o) => o.id === id)) : stored).join(',');
  }
  return {
    name: g.name,
    required: g.required,
    triggerType: g.triggerType,
    applyToPlan: g.applyToPlan,
    firstAppointmentOnly: g.firstAppointmentOnly,
    addAtStages: ids(g.stageTriggers, false),
    addAtStatuses: ids(g.statusTriggers, false),
    addAtApptStages: ids(g.apptTriggers, false),
    removeAtStages: ids(g.stageTriggers, true),
    removeAtStatuses: ids(g.statusTriggers, true),
    removeAtApptStages: ids(g.apptTriggers, true),
    filters,
    items: g.items.map((i) => ({ ...i, key: `i${i.taskItemID}` })),
  };
}

/** Corp's checks: what blocks saving, and what is only warned about. */
export function taskGroupChecks(f: GroupForm, tasks: AdminTask[]): { errors: string[]; suggestions: string[] } {
  const errors: string[] = [];
  if (f.name.trim() === '') errors.push('Enter a group name');
  if (f.triggerType === 0 && f.addAtStages.length === 0) errors.push('Select at least one stage for Plan Stage trigger');
  if (f.triggerType === 5 && f.addAtStatuses.length === 0) errors.push('Select at least one status for Plan Status trigger');
  if (f.triggerType === 1 && f.addAtApptStages.length === 0) errors.push('Select at least one appointment stage for Appointment Stage trigger');
  if (APPLY_TO_TRIGGERS.has(f.triggerType) && f.applyToPlan && csvIds(f.filters.planTypeID).length === 0) {
    errors.push('Select at least one plan type (Apply To Plan is set to Selected Plan Types)');
  }
  const problems = f.items.map(itemProblems);
  if (problems.some((p) => p.missingTask)) errors.push('All task items must have a task selected');
  if (problems.some((p) => p.missingUser)) errors.push('Task items assigned to "User" must have a user selected');
  if (problems.some((p) => p.missingGroup)) errors.push('Task items assigned to "Group" must have a group selected');

  const suggestions: string[] = [];
  if (f.items.length === 0) suggestions.push('This task group has no task items. Without at least one task item, the trigger will not create any tasks.');
  const stranded = f.items.filter((i) => wontSend(i, tasks));
  if (stranded.length > 0) {
    const names = stranded.map((i) => tasks.find((t) => t.taskID === i.taskID)?.name || i.taskName || `Task ${i.taskID}`).join(', ');
    suggestions.push(
      `${stranded.length} automated task${stranded.length === 1 ? '' : 's'} will never be sent: ${names}. Automated tasks (E-Mail, SMS, tags, surveys, stage changes) are only sent when Assigned To is "Automatic".`,
    );
  }
  return { errors, suggestions };
}

/** Corp's trigger bar colours. */
const TRIGGER_BAR: Record<number, { bar: string; border: string }> = {
  0: { bar: 'bg-indigo-50 text-indigo-700', border: 'border-indigo-300' },
  1: { bar: 'bg-amber-50 text-amber-700', border: 'border-amber-300' },
  2: { bar: 'bg-emerald-50 text-emerald-700', border: 'border-emerald-300' },
  3: { bar: 'bg-pink-50 text-pink-700', border: 'border-pink-300' },
  4: { bar: 'bg-cyan-50 text-cyan-700', border: 'border-cyan-300' },
  5: { bar: 'bg-violet-50 text-violet-700', border: 'border-violet-300' },
};

const EVENT_NOTE: Record<number, string> = {
  2: 'Tasks are added on patient entry',
  3: 'Tasks are added when lab results are received',
  4: 'Tasks are added on payment entry',
};

const APPT_OPTIONS = Object.entries(APPT_STATUS_LABELS).map(([value, label]) => ({ value, label }));

/** A collapsible section: a bar with its summary, the fields under it. */
function Section({
  title,
  open,
  onToggle,
  barClass,
  borderClass,
  summary,
  pill,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  barClass: string;
  borderClass: string;
  summary?: ReactNode;
  pill?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={cn('overflow-hidden rounded-lg border-2', borderClass)}>
      <div className={cn('flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium', barClass)}>
        <button type="button" aria-expanded={open} onClick={onToggle} className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-left">
          {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <span className="shrink-0">{title}</span>
          {summary}
        </button>
        {pill}
      </div>
      {open && <div className="space-y-4 bg-white p-4">{children}</div>}
    </div>
  );
}

/** A Trigger Type option: corp's coloured dot and the name. */
function TriggerOption({ type }: { type: number }) {
  const dot = TRIGGER_TYPE_OPTIONS.find((o) => o.value === type)?.dot ?? 'bg-slate-400';
  return (
    <span className="flex items-center gap-2">
      <span className={cn('h-2 w-2 shrink-0 rounded-full', dot)} aria-hidden />
      {TRIGGER_TYPE_LABELS[type] ?? 'Unknown'}
    </span>
  );
}

function Chip({ className, children }: { className?: string; children: ReactNode }) {
  return <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[11px]', className ?? 'bg-slate-200 text-slate-700')}>{children}</span>;
}

/** A multi-select with the picked rows listed under it (corp's trigger lists). */
function PickList({
  id,
  label,
  options,
  selected,
  onChange,
  error,
  badge,
}: {
  id: string;
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  error?: boolean;
  badge?: ReactNode;
}) {
  return (
    <DialogField label={label} htmlFor={id} state={error ? 'error' : 'none'} labelHint={error ? '— required' : undefined} badge={badge}>
      <SearchableMultiSelect
        id={id}
        options={options}
        selectedValues={selected}
        onSelectionChange={onChange}
        placeholder="Select..."
        searchPlaceholder={`Search ${label.toLowerCase()}...`}
        className={error ? fieldClasses.input('error') : undefined}
      />
      <SelectedRows<string>
        rows={selected.map((v) => {
          const o = options.find((x) => x.value === v);
          return o ? { id: v, label: o.label } : { id: v, label: `#${v}`, unavailable: true };
        })}
        removeLabel="Remove"
        onRemove={(v) => onChange(selected.filter((x) => x !== v))}
      />
    </DialogField>
  );
}

export function TaskGroupFormModal({
  taskGroupID,
  tasks: givenTasks,
  onEditTask,
  onClose,
  onSaved,
}: {
  /** null = a new group. */
  taskGroupID: number | null;
  /** The account's tasks (loaded here when not given). */
  tasks?: AdminTask[];
  onEditTask?: (taskID: number) => void;
  onClose: () => void;
  onSaved: (taskGroupID: number) => void;
}) {
  const isNew = taskGroupID === null;
  // Locked: a viewer, so there is nothing to fix and no error or warning shows.
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<GroupForm>(emptyForm, { tracking: !isNew });
  const f = t.form;
  const [group, setGroup] = useState<AdminTaskGroup | null>(null);
  const [opts, setOpts] = useState<TaskGroupFilterOptions | null>(null);
  const [users, setUsers] = useState<AccountUser[]>([]);
  const [loadedTasks, setLoadedTasks] = useState<AdminTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(isNew);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const newItems = useRef(0);
  const { titles } = useRoleTitles();
  const tasks = givenTasks ?? loadedTasks;

  useEffect(() => {
    let live = true;
    void Promise.all([
      taskGroupID === null ? Promise.resolve(null) : getAdminTaskGroup(taskGroupID),
      getTaskGroupFilterOptions(),
      listAccountUsers().catch(() => [] as AccountUser[]),
      givenTasks ? Promise.resolve(givenTasks) : listAdminTasks().catch(() => [] as AdminTask[]),
    ])
      .then(([g, o, u, tk]) => {
        if (!live) return;
        setGroup(g);
        setOpts(o);
        setUsers(u);
        setLoadedTasks(tk);
        if (g) t.reset(formFrom(g, o));
        setLoading(false);
      })
      .catch((e) => {
        if (!live) return;
        toast.error(getErrorMessage(e, 'The task group could not be loaded.'));
        onClose();
      });
    return () => {
      live = false;
    };
    // Loads once per open; the page passes a new key for another group.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskGroupID]);

  const type = f.triggerType;
  const { errors, suggestions } = taskGroupChecks(f, tasks);
  const missingTrigger =
    !locked && errors.some((e) => e.startsWith('Select at least one stage') || e.startsWith('Select at least one status') || e.startsWith('Select at least one appointment'));
  const missingPlanType = !locked && errors.some((e) => e.startsWith('Select at least one plan type'));
  const missingName = !locked && f.name.trim() === '';

  const role = (key: string, fallback: string) => titles[key] ?? fallback;
  const assignedToOptions = ASSIGNED_TO_OPTIONS.map((o) =>
    'roleType' in o && titles[o.roleType] ? { value: o.value, label: `${titles[o.roleType]} From Plan` } : { value: o.value, label: o.label },
  );

  const stageOptions = useMemo(() => (opts?.stages ?? []).map((s) => ({ value: s.id, label: s.name })), [opts]);
  const statusOptions = useMemo(() => (opts?.stageSubs ?? []).map((s) => ({ value: s.id, label: s.name })), [opts]);

  // ── Section summaries and change tracking ──
  const saved = t.saved;
  const triggerChanged =
    !isNew &&
    (((['triggerType', 'addAtStages', 'addAtStatuses', 'addAtApptStages', 'applyToPlan', 'firstAppointmentOnly'] as const).some((k) => t.changed.has(k))) ||
      f.filters.planTypeID !== saved.filters.planTypeID);
  const removeChanged = !isNew && (['removeAtStages', 'removeAtStatuses', 'removeAtApptStages'] as const).some((k) => t.changed.has(k));
  const filtersChanged = !isNew && FILTER_KEYS.some((k) => k !== 'planTypeID' && f.filters[k] !== saved.filters[k]);

  const addAtNames = (() => {
    const pick = (ids: string[], list: { value: string; label: string }[]) => ids.map((id) => list.find((o) => o.value === id)?.label ?? `#${id}`);
    if (type === 0) return pick(f.addAtStages, stageOptions);
    if (type === 5) return pick(f.addAtStatuses, statusOptions);
    if (type === 1) return pick(f.addAtApptStages, APPT_OPTIONS);
    return [];
  })();

  const filterLabel = (key: FilterKey): string =>
    ({
      planTypeID: 'Plan Type',
      doctorID: role('Doctor', 'Doctor'),
      coordID: role('Coordinator', 'Coordinator'),
      nurseID: role('Nurse', 'Nurse'),
      facilityID: type === 4 ? 'Payment Facility' : 'Plan Facility',
      procedureID: 'Procedure',
      productID: 'Product',
      membershipID: 'Membership',
      packageID: 'Package',
      leadSourceID: 'Lead Source',
      languageID: 'Language',
      appointmentTypeID: 'Appt Type',
      appointmentTypeGroupID: 'Appt Group',
      currentStatus: 'Appt Status',
      appointmentFacilityID: 'Appt Facility',
      tagID: 'Tags',
      extraFilters: 'Other',
    })[key];
  const filterOptions = (key: FilterKey): FilterOption[] => {
    const optionKey = FILTER_OPTION_KEY[key];
    return optionKey ? (opts?.[optionKey] ?? []) : EXTRA_FILTER_OPTIONS;
  };
  const activeFilters = FILTER_KEYS.map((key) => ({ key, count: csvIds(f.filters[key]).length, total: filterOptions(key).length })).filter((x) => x.count > 0);
  const setFilter = (key: FilterKey, ids: string[]) => t.patch({ filters: { ...f.filters, [key]: ids.join(',') } });

  function revertTrigger() {
    t.patch({
      triggerType: saved.triggerType,
      addAtStages: saved.addAtStages,
      addAtStatuses: saved.addAtStatuses,
      addAtApptStages: saved.addAtApptStages,
      applyToPlan: saved.applyToPlan,
      firstAppointmentOnly: saved.firstAppointmentOnly,
      filters: { ...f.filters, planTypeID: saved.filters.planTypeID },
    });
  }

  // ── Items ──
  const savedItems = useMemo(() => new Map(saved.items.map((i) => [i.key, i])), [saved.items]);

  function addItem() {
    newItems.current += 1;
    const key = `n${newItems.current}`;
    const item: FormItem = {
      key,
      taskItemID: 0,
      taskID: 0,
      taskName: '',
      taskAutoID: 0,
      timeType: 1,
      timeAmount: 0,
      recurringType: 1,
      recurringAmount: 1,
      dateType: 1,
      assignedToID: 'coord',
      priority: 1,
      recurring: false,
      recurringNumber: 2,
      customDate: '',
      sendToID: 0,
    };
    t.patch({ items: [...f.items, item] });
    setOpenItems((prev) => new Set(prev).add(key));
  }

  function setItem(next: FormItem) {
    t.patch({ items: f.items.map((i) => (i.key === next.key ? next : i)) });
  }

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      const { taskGroupID: id, failures } = await saveTaskGroup(group, f);
      if (failures.length > 0) {
        toast.error(`Task group partially saved — ${failures.length} change${failures.length === 1 ? '' : 's'} failed: ${failures.join('; ')}`);
      } else {
        toast.success(group ? `"${f.name.trim()}" updated successfully` : 'Task group created');
      }
      onSaved(id);
    } catch (e) {
      toast.error(getErrorMessage(e, 'The task group could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const bar = missingTrigger || missingPlanType ? { bar: 'bg-red-50 text-red-700', border: fieldClasses.input('error') } : (TRIGGER_BAR[type] ?? TRIGGER_BAR[0]);
  const removeCount = f.removeAtStages.length + f.removeAtStatuses.length + f.removeAtApptStages.length;
  const itemsHaveErrors = !locked && f.items.some((i) => {
    const p = itemProblems(i);
    return p.missingTask || p.missingUser || p.missingGroup;
  });
  // The amber "no items" nudge asks for an Add Item, which a Locked page doesn't have.
  const noItemsWarning = !locked && f.items.length === 0;

  return (
    <AdminFormModal
      title={isNew ? 'New Task Group' : 'Edit Task Group'}
      isNew={isNew}
      dirty={isNew ? t.changed.size > 0 : t.dirty}
      saving={saving}
      errors={loading ? [] : errors}
      suggestions={loading ? [] : suggestions}
      saveLabel={isNew ? 'Create' : 'Save'}
      canSave={!loading}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="xxl"
    >
      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid items-end gap-4 md:grid-cols-[1fr_auto]">
            <DialogField
              label="Group Name"
              htmlFor="task-group-name"
              required
              state={missingName ? 'error' : 'none'}
              labelHint={missingName ? '— required' : undefined}
              badge={t.badge('name', 'Group Name')}
            >
              <Input
                id="task-group-name"
                value={f.name}
                maxLength={50}
                placeholder="Task group name"
                onChange={(e) => t.patch({ name: e.target.value })}
                className={fieldClasses.input(missingName ? 'error' : 'none')}
              />
            </DialogField>
            <DialogField label="Complete Before Due" htmlFor="task-group-required" badge={t.badge('required', 'Complete Before Due')}>
              <Select<string>
                id="task-group-required"
                options={[
                  { value: '0', label: 'Allowed' },
                  { value: '1', label: 'Not Allowed' },
                ]}
                value={f.required ? '1' : '0'}
                onChange={(v) => t.patch({ required: v === '1' })}
                wrapperClassName="w-[150px]"
              />
            </DialogField>
          </div>

          <Section
            title={TRIGGER_TYPE_LABELS[type] ?? 'Unknown'}
            open={triggerOpen}
            onToggle={() => setTriggerOpen((v) => !v)}
            barClass={bar.bar}
            borderClass={bar.border}
            pill={triggerChanged ? <ModifiedPill label="trigger" onRevert={revertTrigger} /> : null}
            summary={
              EVENT_TRIGGERS.has(type) ? (
                <>
                  <span className="shrink-0 opacity-40">—</span>
                  <span className="text-xs font-normal italic opacity-70">{EVENT_NOTE[type]}</span>
                </>
              ) : addAtNames.length > 0 ? (
                <>
                  <span className="shrink-0 opacity-40">—</span>
                  {addAtNames.slice(0, 4).map((n, i) => (
                    <Chip key={`${n}-${i}`} className={cn('font-bold', bar.bar)}>
                      {n}
                    </Chip>
                  ))}
                  {addAtNames.length > 4 && <span className="whitespace-nowrap text-xs opacity-60">+{addAtNames.length - 4} more</span>}
                </>
              ) : null
            }
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <DialogField label="Trigger Type" htmlFor="task-group-trigger" badge={t.badge('triggerType', 'Trigger Type')}>
                  <Select<number>
                    id="task-group-trigger"
                    options={TRIGGER_TYPE_OPTIONS.map((o) => ({ value: o.value, label: TRIGGER_TYPE_LABELS[o.value] }))}
                    value={type}
                    onChange={(v) => v != null && t.patch({ triggerType: v })}
                    renderOption={(o) => <TriggerOption type={o.value} />}
                    renderValue={(o) => <TriggerOption type={o.value} />}
                    wrapperClassName="w-fit"
                  />
                </DialogField>
                {type === 0 && (
                  <PickList id="task-group-add-stages" label="Add At Stage" options={stageOptions} selected={f.addAtStages} onChange={(v) => t.patch({ addAtStages: v })} error={missingTrigger} badge={t.badge('addAtStages', 'Add At Stage')} />
                )}
                {type === 5 && (
                  <PickList id="task-group-add-statuses" label="Add At Status" options={statusOptions} selected={f.addAtStatuses} onChange={(v) => t.patch({ addAtStatuses: v })} error={missingTrigger} badge={t.badge('addAtStatuses', 'Add At Status')} />
                )}
                {type === 1 && (
                  <PickList id="task-group-add-appt" label="Add At Appointment Stage" options={APPT_OPTIONS} selected={f.addAtApptStages} onChange={(v) => t.patch({ addAtApptStages: v })} error={missingTrigger} badge={t.badge('addAtApptStages', 'Add At Appointment Stage')} />
                )}
                {EVENT_TRIGGERS.has(type) && <p className="text-xs italic text-slate-500">{EVENT_NOTE[type]}.</p>}
              </div>
              <div className="space-y-3">
                {type === 1 && (
                  <div className="flex items-start gap-3">
                    <DialogField label="Appointment" htmlFor="task-group-first-appt" badge={t.badge('firstAppointmentOnly', 'Appointment')} className="w-48 shrink-0">
                      <Select<string>
                        id="task-group-first-appt"
                        options={[
                          { value: '0', label: 'Any appointment' },
                          { value: '1', label: 'First Time appointment' },
                        ]}
                        value={f.firstAppointmentOnly ? '1' : '0'}
                        onChange={(v) => t.patch({ firstAppointmentOnly: v === '1' })}
                        wrapperClassName="w-fit"
                      />
                    </DialogField>
                    <p className="pt-0.5 text-xs leading-snug text-slate-500">
                      {f.firstAppointmentOnly
                        ? 'Tasks are added only when the appointment is marked First Time. The scheduler marks a patient’s first appointment of each type automatically, and staff can change it on the appointment.'
                        : 'Tasks are added every time an appointment reaches the stage selected above.'}
                    </p>
                  </div>
                )}
                {APPLY_TO_TRIGGERS.has(type) && (
                  <>
                    <div className="flex items-start gap-3">
                      <DialogField label="Apply To" htmlFor="task-group-apply-to" badge={t.badge('applyToPlan', 'Apply To')} className="w-48 shrink-0">
                        <Select<string>
                          id="task-group-apply-to"
                          options={[
                            { value: '0', label: 'Associated Plan' },
                            { value: '1', label: 'Selected Plan Types' },
                          ]}
                          value={f.applyToPlan ? '1' : '0'}
                          onChange={(v) =>
                            v === '1' ? t.patch({ applyToPlan: true }) : t.patch({ applyToPlan: false, filters: { ...f.filters, planTypeID: '' } })
                          }
                          wrapperClassName="w-fit"
                        />
                      </DialogField>
                      <p className="pt-0.5 text-xs leading-snug text-slate-500">
                        {f.applyToPlan
                          ? 'Tasks will apply to plans matching the selected plan types below. At least one plan type must be selected.'
                          : 'Tasks will apply to the plan directly associated with the triggering event (e.g. the plan linked to the payment, appointment, or lab result).'}
                      </p>
                    </div>
                    {f.applyToPlan && (
                      <PickList
                        id="task-group-apply-plan-types"
                        label="Plan Types"
                        options={(opts?.planTypes ?? []).map((p) => ({ value: p.id, label: p.name }))}
                        selected={csvIds(f.filters.planTypeID)}
                        onChange={(v) => setFilter('planTypeID', v)}
                        error={missingPlanType}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </Section>

          <Section
            title="Remove At"
            open={removeOpen}
            onToggle={() => setRemoveOpen((v) => !v)}
            barClass="bg-slate-100 text-slate-700"
            borderClass={removeCount > 0 ? fieldClasses.dashed('error') : 'border-slate-300'}
            pill={
              removeChanged ? (
                <ModifiedPill
                  label="remove at"
                  onRevert={() => t.patch({ removeAtStages: saved.removeAtStages, removeAtStatuses: saved.removeAtStatuses, removeAtApptStages: saved.removeAtApptStages })}
                />
              ) : null
            }
            summary={
              removeCount > 0 ? (
                <>
                  <span className="shrink-0 opacity-40">—</span>
                  {f.removeAtStages.length > 0 && <Chip>Plan Stage {f.removeAtStages.length}/{stageOptions.length}</Chip>}
                  {f.removeAtStatuses.length > 0 && <Chip>Plan Status {f.removeAtStatuses.length}/{statusOptions.length}</Chip>}
                  {f.removeAtApptStages.length > 0 && <Chip>Appt Stage {f.removeAtApptStages.length}/{APPT_OPTIONS.length}</Chip>}
                </>
              ) : null
            }
          >
            <div className="grid gap-4 md:grid-cols-3">
              <PickList id="task-group-remove-stages" label="Plan Stage" options={stageOptions} selected={f.removeAtStages} onChange={(v) => t.patch({ removeAtStages: v })} badge={t.badge('removeAtStages', 'Plan Stage')} />
              <PickList id="task-group-remove-statuses" label="Plan Status" options={statusOptions} selected={f.removeAtStatuses} onChange={(v) => t.patch({ removeAtStatuses: v })} badge={t.badge('removeAtStatuses', 'Plan Status')} />
              <PickList id="task-group-remove-appt" label="Appointment Stage" options={APPT_OPTIONS} selected={f.removeAtApptStages} onChange={(v) => t.patch({ removeAtApptStages: v })} badge={t.badge('removeAtApptStages', 'Appointment Stage')} />
            </div>
          </Section>

          <Section
            title="Filters"
            open={filtersOpen}
            onToggle={() => setFiltersOpen((v) => !v)}
            barClass="bg-slate-100 text-slate-700"
            borderClass={activeFilters.length > 0 ? 'border-blue-400' : 'border-slate-300'}
            pill={filtersChanged ? <ModifiedPill label="filters" onRevert={() => t.patch({ filters: { ...saved.filters } })} /> : null}
            summary={
              activeFilters.length > 0 ? (
                <>
                  <span className="shrink-0 opacity-40">—</span>
                  {activeFilters.map((x) => (
                    <Chip key={x.key}>
                      {filterLabel(x.key)} {x.count}/{x.total}
                    </Chip>
                  ))}
                </>
              ) : null
            }
          >
            <div className="grid gap-2 md:grid-cols-3">
              {FILTER_KEYS.map((key) => {
                const options = filterOptions(key);
                const selected = csvIds(f.filters[key]);
                const picked = selected.length > 0;
                const id = `task-group-filter-${key}`;
                return (
                  <div key={key} className={cn('rounded-md border p-2 transition-colors', picked ? 'border-blue-500 bg-blue-50/60' : 'border-slate-200 bg-white')}>
                    <div className="mb-1 flex flex-wrap items-center gap-1">
                      <label htmlFor={id} className={cn('text-xs font-medium', picked ? 'text-blue-800' : 'text-slate-600')}>
                        {filterLabel(key)}
                        {picked && (
                          <span className="ml-1 font-semibold">
                            ({selected.length}/{options.length})
                          </span>
                        )}
                      </label>
                      {!isNew && f.filters[key] !== saved.filters[key] && (
                        <ModifiedPill label={filterLabel(key)} onRevert={() => setFilter(key, csvIds(saved.filters[key]))} />
                      )}
                    </div>
                    <SearchableMultiSelect
                      id={id}
                      options={options.map((o) => ({ value: o.id, label: o.name, ...(o.category ? { subtitle: o.category } : {}) }))}
                      selectedValues={selected}
                      onSelectionChange={(v) => setFilter(key, v)}
                      placeholder={key === 'facilityID' || key === 'appointmentFacilityID' ? 'All Facilities' : 'None'}
                      searchPlaceholder={`Search ${filterLabel(key).toLowerCase()}...`}
                    />
                  </div>
                );
              })}
            </div>
          </Section>

          <div
            className={cn(
              'space-y-4 rounded-lg border-2 bg-white p-4',
              itemsHaveErrors ? fieldClasses.input('error') : noItemsWarning ? fieldClasses.input('warning') : 'border-slate-400',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <p className={cn(LABEL_CLASS, 'font-semibold', itemsHaveErrors ? 'text-red-700' : noItemsWarning ? 'text-amber-700' : 'text-slate-700')}>
                <span>Task Items</span>
                {itemsHaveErrors && <span className="text-xs font-normal text-red-600">— has errors</span>}
                {!itemsHaveErrors && noItemsWarning && <span className="text-xs font-normal text-amber-500">— recommended</span>}
                {t.badge('items', 'Task Items')}
              </p>
              {!locked && (
                <Button variant="secondary" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={addItem}>
                  Add Item
                </Button>
              )}
            </div>
            {f.items.length === 0 ? (
              noItemsWarning ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  This task group has no task items. Without at least one task item, the trigger will not create any tasks. Click <strong>Add Item</strong> to add one.
                </p>
              ) : (
                <p className="text-sm text-slate-500">This task group has no task items.</p>
              )
            ) : (
              <div className="space-y-2">
                {f.items.map((item, idx) => (
                  <TaskItemCard
                    key={item.key}
                    item={item}
                    saved={savedItems.get(item.key)}
                    index={idx}
                    tasks={tasks}
                    users={users}
                    roles={opts?.roles ?? []}
                    assignedToOptions={assignedToOptions}
                    open={openItems.has(item.key)}
                    onToggle={() =>
                      setOpenItems((prev) => {
                        const next = new Set(prev);
                        if (next.has(item.key)) next.delete(item.key);
                        else next.add(item.key);
                        return next;
                      })
                    }
                    onChange={setItem}
                    onRemove={() => t.patch({ items: f.items.filter((i) => i.key !== item.key) })}
                    onRevert={() => {
                      const before = savedItems.get(item.key);
                      if (before) setItem(before);
                    }}
                    onEditTask={onEditTask}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminFormModal>
  );
}

